import { useState } from 'react';
import type { ReactNode } from 'react';
import type {
  ActionGroup,
  ActionItem,
  ActionTargetType,
  AutoChecklist,
  BadgeTone,
  BuitenlandTwijfel,
  ChecklistStap,
  DataGat,
} from '@fleet/shared';
import { euro, fmtDate } from '@fleet/shared';
import { Badge } from '../../design-system';
import { InfoTip } from '../../components/ui/InfoTip';
import { MeerTonen } from '../../components/ui/MeerTonen';
import { AutoLink, PersoonLink } from '../../components/ui/EntiteitLink';
import { Panel } from '../../components/ui/Panel';
import { Voortgang } from '../../components/ui/Voortgang';
import {
  AutoChecklistInhoud,
  AutoChecklistTitel,
  type OpStapKlik,
} from '../car-detail/AutoChecklist';

const MONO = "'Roboto Mono', monospace";

/** Items shown when a card is opened; the rest sit behind the toggle. */
const CAP = 6;

const TONE_COLOR: Record<BadgeTone, string> = {
  danger: '#B0543B',
  warning: '#E0A943',
  success: '#2A7050',
  blue: '#2A6085',
  neutral: '#C9BBA8',
};

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      aria-hidden
      style={{
        flex: 'none',
        transform: open ? 'rotate(90deg)' : 'none',
        transition: 'transform .18s ease',
        color: '#AAA093',
      }}
    >
      <path
        d="M6 3.5L10.5 8L6 12.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * One subject, one card.
 *
 * These used to be rows inside a single bordered panel with an accent stripe
 * down the side. Two problems: the stripe and the panel title repeated a heading
 * the page already carries, and rows inside one box read as a table, which
 * invites you to compare an expiring MOT against a count of missing policy
 * numbers. They are not comparable. Separate cards say so.
 *
 * Collapsed a card is one line whatever it holds, so twenty-six missing policy
 * numbers cost exactly as much room as one MOT.
 */
function Kaart({
  kleur,
  titel,
  hint,
  rechts,
  aantal,
  ariaTitel,
  children,
}: {
  kleur: string;
  titel: ReactNode;
  /** Only where the consequence is not obvious; most cards have none. */
  hint?: string;
  rechts: ReactNode;
  aantal: number;
  /** Plain-text version of `titel`, for the toggle's label. */
  ariaTitel?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Panel padding={0}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto auto 1fr',
          alignItems: 'center',
          gap: 10,
          padding: '0 18px',
        }}
      >
        <span
          style={{ width: 8, height: 8, borderRadius: '50%', background: kleur, flex: 'none' }}
        />
        <span style={{ fontSize: 13.5, fontWeight: 700, color: '#111111', whiteSpace: 'nowrap' }}>
          {titel}
        </span>
        {/* De toelichting valt buiten de klapknop: een knop in een knop kan niet,
            en zweven over het icoon hoort de kaart niet open te trekken. */}
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {hint && <InfoTip tekst={hint} uitlijning="links" />}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={`${ariaTitel ?? ''} ${open ? 'dichtklappen' : 'uitklappen'}`.trim()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 10,
              flex: 1,
              minWidth: 0,
              // De hele rechterkant is klikbaar, niet alleen het pijltje.
              padding: '14px 0',
              background: 'none',
              border: 'none',
              font: 'inherit',
              cursor: 'pointer',
            }}
          >
            {rechts}
            <Chevron open={open} />
          </button>
        </span>
      </div>
      {open && aantal > 0 && <div style={{ padding: '0 18px 14px 26px' }}>{children}</div>}
    </Panel>
  );
}

function ItemRegels({
  items,
  onNavigate,
}: {
  items: ActionItem[];
  onNavigate: (t: ActionTargetType, id: string) => void;
}) {
  return (
    <MeerTonen items={items} cap={CAP} meer={(n) => `+${n} meer tonen`}>
      {(zicht) =>
        zicht.map((it) => (
          <div
            key={`${it.targetType}-${it.id}`}
            onClick={() => onNavigate(it.targetType, it.id)}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(72px, auto) 1fr auto',
              alignItems: 'center',
              gap: 10,
              padding: '6px 0',
              fontSize: 12.5,
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                fontWeight: 700,
                color: '#2A6085',
                fontFamily: it.targetType === 'persoon' ? 'inherit' : MONO,
                fontSize: it.targetType === 'persoon' ? 12.5 : 11.5,
                whiteSpace: 'nowrap',
              }}
            >
              {it.primary}
            </span>
            <span
              style={{
                color: '#777777',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {it.secondary}
            </span>
            {it.badge ? <Badge tone={it.tone}>{it.badge}</Badge> : <span />}
          </div>
        ))
      }
    </MeerTonen>
  );
}

/** Wie, met welke auto's en wanneer — de bundel in één regel. */
function samenvatting(regels: BuitenlandTwijfel['transacties']): string {
  const kentekens = [...new Set(regels.map((r) => r.kenteken))];
  const personen = [...new Set(regels.map((r) => r.persoonNaam).filter(Boolean))];
  // De transacties staan op datum, dus de eerste en de laatste staan aan de uiteinden.
  const eerste = regels[0]?.datum ?? '';
  const laatste = regels[regels.length - 1]?.datum ?? '';
  return [
    `${regels.length}${regels.length === 1 ? ' transactie' : ' transacties'}`,
    personen.join(', ') || null,
    kentekens.join(', '),
    eerste === laatste ? fmtDate(laatste) : `${fmtDate(eerste)} t/m ${fmtDate(laatste)}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Tankbeurten op een naam die aan beide kanten van de grens ligt, met de vraag welke
 * kant het was.
 *
 * **Het antwoord hangt aan de transacties en niet aan de plaatsnaam.** Dat is het
 * verschil met hoe dit eerst werkte, en het is geen detail: een oordeel op de naam is
 * een regel die vooruit werkt. Wie één keer zei dat Hoogland het Nederlandse was, liet
 * élke volgende Hoogland stil door — ook die van de collega die er twee maanden later
 * een week in de Belgische versie rondreed. Die tankbeurt kwam dan nooit meer langs.
 *
 * Gebundeld per plaats omdat negen keer Hoogland anders negen keer klikken is. De
 * bundelknop beantwoordt precies de regels die je eronder ziet staan en niets meer;
 * verschijnt die naam volgende maand weer, dan staat de vraag er gewoon weer.
 *
 * En omdat een bundel gemengd kan zijn — twee collega's, twee Hooglands, dezelfde week
 * — is hij open te klappen en heeft elke tankbeurt zijn eigen twee knoppen.
 */
function TwijfelRegels({
  twijfel,
  onOordeel,
  bezig,
}: {
  twijfel: BuitenlandTwijfel[];
  onOordeel: (costLineIds: string[], oordeel: 'NEDERLAND' | 'BUITENLAND') => void;
  bezig: boolean;
}) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div>
      {twijfel.map((t) => {
        const uit = open === t.plaats;
        const ids = t.transacties.map((r) => r.id);
        return (
          <div key={t.plaats}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(96px, auto) 1fr auto auto',
                alignItems: 'center',
                gap: 10,
                padding: '6px 0',
                fontSize: 12.5,
              }}
            >
              <span style={{ fontWeight: 700, color: '#111111' }}>{t.plaatsRuw}</span>
              {/* Bij één transactie valt er niets uit te splitsen: de regel is de
                  transactie, en een klapknop zou alleen hetzelfde nog eens tonen. */}
              {t.transacties.length > 1 ? (
                <button
                  type="button"
                  onClick={() => setOpen(uit ? null : t.plaats)}
                  aria-expanded={uit}
                  title="Per tankbeurt beslissen"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    minWidth: 0,
                    padding: 0,
                    background: 'none',
                    border: 'none',
                    font: 'inherit',
                    fontSize: 12.5,
                    color: '#777777',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {samenvatting(t.transacties)}
                  </span>
                  <Chevron open={uit} />
                </button>
              ) : (
                <span
                  style={{
                    color: '#777777',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {samenvatting(t.transacties)}
                </span>
              )}
              <span
                style={{
                  color: '#111111',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {euro(t.bedragExcl)}
              </span>
              <span style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
                <TwijfelKnop
                  label="Nederland"
                  titel={
                    t.transacties.length === 1
                      ? `Deze tankbeurt was in het Nederlandse ${t.plaatsRuw}`
                      : `Alle ${t.transacties.length} waren in het Nederlandse ${t.plaatsRuw}`
                  }
                  bezig={bezig}
                  onClick={() => onOordeel(ids, 'NEDERLAND')}
                />
                <TwijfelKnop
                  label="Buitenland"
                  titel={
                    t.transacties.length === 1
                      ? `Deze tankbeurt lag over de grens — zet hem bij Tanken en laden`
                      : `Alle ${t.transacties.length} lagen over de grens — zet ze bij Tanken en laden`
                  }
                  bezig={bezig}
                  onClick={() => onOordeel(ids, 'BUITENLAND')}
                />
              </span>
            </div>

            {uit &&
              t.transacties.map((r) => (
                <div
                  key={r.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(96px, auto) 1fr auto auto',
                    alignItems: 'center',
                    gap: 10,
                    padding: '5px 0',
                    fontSize: 12,
                    borderTop: '1px solid #F0E8DC',
                  }}
                >
                  <span
                    style={{
                      color: '#777777',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                      paddingLeft: 12,
                    }}
                  >
                    {fmtDate(r.datum)}
                    {r.tijd && <span style={{ color: '#AAA093' }}> {r.tijd}</span>}
                  </span>
                  <span
                    style={{
                      color: '#777777',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <AutoLink kenteken={r.kenteken} style={{ fontSize: 11 }} />
                    {' · '}
                    {r.persoonId && r.persoonNaam ? (
                      <PersoonLink persoonId={r.persoonId} naam={r.persoonNaam} />
                    ) : (
                      (r.persoonNaam ?? 'geen bestuurder')
                    )}
                  </span>
                  <span
                    style={{
                      color: '#777777',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {euro(r.bedragExcl)}
                  </span>
                  <span style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
                    <TwijfelKnop
                      label="Nederland"
                      titel={`Deze ene tankbeurt was in het Nederlandse ${t.plaatsRuw}`}
                      bezig={bezig}
                      onClick={() => onOordeel([r.id], 'NEDERLAND')}
                    />
                    <TwijfelKnop
                      label="Buitenland"
                      titel={`Deze ene tankbeurt lag over de grens`}
                      bezig={bezig}
                      onClick={() => onOordeel([r.id], 'BUITENLAND')}
                    />
                  </span>
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );
}

function TwijfelKnop({
  label,
  titel,
  bezig,
  onClick,
}: {
  label: string;
  titel: string;
  bezig: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={bezig}
      title={titel}
      style={{
        padding: '3px 9px',
        border: '1px solid #C9BBA8',
        borderRadius: 2,
        background: '#FFFFFF',
        font: 'inherit',
        fontSize: 11.5,
        fontWeight: 700,
        color: bezig ? '#AAA093' : '#2A6085',
        cursor: bezig ? 'wait' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}

/** The cards stack tighter than the page gap, so they read as one group. */
const STAPEL = { display: 'flex', flexDirection: 'column', gap: 10 } as const;

export function ActionCenter({
  groups,
  dataGaten,
  autoChecklists,
  buitenlandTwijfel,
  onNavigate,
  onChecklistStap,
  onChecklistToggle,
  onBuitenlandOordeel,
  buitenlandBezig = false,
}: {
  groups: ActionGroup[];
  dataGaten: DataGat[];
  autoChecklists: AutoChecklist[];
  /** Tankbeurten op een naam die ook over de grens bestaat; wachten op een antwoord. */
  buitenlandTwijfel: BuitenlandTwijfel[];
  onNavigate: (targetType: ActionTargetType, id: string) => void;
  onChecklistStap: OpStapKlik;
  onChecklistToggle: (kenteken: string, stap: ChecklistStap) => void;
  onBuitenlandOordeel: (costLineIds: string[], oordeel: 'NEDERLAND' | 'BUITENLAND') => void;
  buitenlandBezig?: boolean;
}) {
  const leeg =
    groups.length === 0 &&
    dataGaten.length === 0 &&
    autoChecklists.length === 0 &&
    buitenlandTwijfel.length === 0;

  return (
    <div style={STAPEL}>
      {leeg && (
        <Panel padding={16}>
          <span style={{ fontSize: 13, color: '#2A7050', fontWeight: 700 }}>
            Niets dat aandacht vraagt.
          </span>
        </Panel>
      )}

      {/* Een nieuwe auto staat bovenaan: alles eronder gaat over auto's die al in de
          app staan, en zolang deze stappen niet gedaan zijn kloppen die cijfers niet.
          Eerst de auto compleet maken, dan de rest lezen. */}
      {autoChecklists.map((c) => (
        <Kaart
          key={c.kenteken}
          kleur={c.gedaan === 0 ? '#B0543B' : '#E0A943'}
          titel={
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
              <span>Nieuwe auto</span>
              <AutoChecklistTitel checklist={c} />
            </span>
          }
          ariaTitel={`Nieuwe auto ${c.kenteken}`}
          hint="Drie stappen vink je zelf af, die gebeuren buiten de app. De rest heeft een grijs vinkje: dat gaat om zodra het in de app staat — klik op de stap om naar de juiste pagina te gaan. Afgevinkte stappen zakken naar beneden, en zodra alles staat verdwijnt de kaart."
          aantal={c.stappen.length}
          rechts={<Voortgang gevuld={c.gedaan} totaal={c.totaal} />}
        >
          <AutoChecklistInhoud
            checklist={c}
            onNavigate={onChecklistStap}
            onToggle={onChecklistToggle}
          />
        </Kaart>
      ))}

      {groups.map((g) => (
        <Kaart
          key={g.key}
          kleur={TONE_COLOR[g.tone]}
          titel={g.title}
          ariaTitel={g.title}
          aantal={g.items.length}
          rechts={<Badge tone={g.tone}>{g.items.length}</Badge>}
        >
          <ItemRegels items={g.items} onNavigate={onNavigate} />
        </Kaart>
      ))}

      {/* Onder de acties en boven de gegevens: het is een vraag met een aanleiding
          (er is over de grens getankt, of niet) maar zonder deadline. Zodra hij
          beantwoord is verdwijnt hij, en verschijnen de transacties bij Tanken en
          laden — of nergens meer. */}
      {buitenlandTwijfel.length > 0 && (
        <Kaart
          kleur="#E0A943"
          titel="Buitenland of niet?"
          ariaTitel="Buitenland of niet"
          hint="Deze plaatsnamen bestaan in Nederland én over de grens — Hasselt, Essen, Beek. Aan de naam alleen is niet te zien welke het was, en de factuur noemt geen land. Je antwoord geldt alleen voor de tankbeurten die je hier ziet staan: komt dezelfde naam op een volgende factuur voor, dan is dat een nieuwe rit en dus een nieuwe vraag. Klap een plaats open om per tankbeurt te beslissen."
          aantal={buitenlandTwijfel.length}
          rechts={
            <Badge tone="warning">
              {buitenlandTwijfel.reduce((n, t) => n + t.transacties.length, 0)}
            </Badge>
          }
        >
          <TwijfelRegels
            twijfel={buitenlandTwijfel}
            onOordeel={onBuitenlandOordeel}
            bezig={buitenlandBezig}
          />
        </Kaart>
      )}

      {/* Ontbrekende gegevens staan in dezelfde stapel: het blijft werk dat
          gedaan moet worden. Wel eronder, want een aflopende APK heeft een datum
          en een leeg polisnummer niet. */}
      {dataGaten.map((d) => (
        <Kaart
          key={d.key}
          kleur={d.gevuld === 0 ? '#B0543B' : '#E0A943'}
          titel={d.label}
          ariaTitel={d.label}
          hint={d.hint}
          aantal={d.items.length}
          rechts={<Voortgang gevuld={d.gevuld} totaal={d.totaal} />}
        >
          <ItemRegels items={d.items} onNavigate={onNavigate} />
        </Kaart>
      ))}
    </div>
  );
}
